<template>
	<div
		ref="actionMenu"
		class="menu d-flex no-margin"
		:class="{
			'mobile-overflow-toolbar': display.mobile.value,
			'mobile-overflow-toolbar--ready': !display.mobile.value || overflowMeasurementReady
		}">
		<input
			v-if="entityType === 'conversation'"
			ref="filePicker"
			type="file"
			:multiple="true"
			:accept="accept"
			class="d-none"
			@change="handleFileChange" />
		<div
			v-if="hasScribeFeature"
			data-test="run-report"
			data-overflow-action="runReport"
			class="d-flex flex-column menu-action"
			:class="{ 'mobile-action-collapsed': isActionCollapsed('runReport') }"
			:aria-hidden="isActionCollapsed('runReport') ? 'true' : undefined">
			<v-tooltip
				z-index="2000"
				:disabled="display.smAndDown.value"
				location="top"
				:open-on-hover="!DeviceUtility.isTouch"
				:open-on-click="false">
				<template #activator="{ props }">
					<div class="text-center d-flex flex-column justify-center align-center">
						<v-btn
							v-test="'run-report'"
							v-track="'run-report-button'"
							icon
							aria-label="Run a Spark"
							elevation="0"
							@click="emit('runReport')"
							v-bind="props">
							<SeanIcon :height="28" :width="28" :is-running="scribeRunning" :enable-tooltip="false" />
						</v-btn>
						<div class="text">Run a Spark</div>
					</div>
				</template>
				<span>Run a Spark to create a report</span>
			</v-tooltip>
		</div>
		<div
			data-overflow-action="comments"
			class="d-flex flex-column menu-action"
			:class="{ 'mobile-action-collapsed': isActionCollapsed('comments') }"
			:aria-hidden="isActionCollapsed('comments') ? 'true' : undefined">
			<v-tooltip
				z-index="2000"
				:disabled="display.smAndDown.value"
				location="top"
				:open-on-hover="!DeviceUtility.isTouch"
				:open-on-click="false">
				<template #activator="{ props }">
					<div class="text-center d-flex flex-column justify-center align-center">
						<v-btn
							v-test="'add-comments'"
							v-track="'add-comments-button'"
							v-bind="props"
							icon
							elevation="0"
							@click="
								emit('toggleComments');
								blurActiveElement();
							">
							<v-icon>icon-chat-stroke</v-icon>
						</v-btn>
						<div class="text">Comments</div>
					</div>
				</template>
				<span>Add new comments</span>
			</v-tooltip>
		</div>
		<div
			v-if="conversationType === 'async'"
			data-overflow-action="addRecording"
			class="d-flex flex-column menu-action"
			:class="{ 'mobile-action-collapsed': isActionCollapsed('addRecording') }"
			:aria-hidden="isActionCollapsed('addRecording') ? 'true' : undefined">
			<v-tooltip
				z-index="2000"
				:disabled="display.smAndDown.value"
				location="top"
				:open-on-hover="!DeviceUtility.isTouch"
				:open-on-click="false"
				:open-on-focus="false">
				<template #activator="{ props }">
					<div class="text-center d-flex flex-column justify-center align-center" v-bind="props">
						<v-btn
							id="add-recording"
							v-test="'add-recording'"
							v-track="'add-recording-button'"
							icon
							elevation="0"
							@click="recordMore">
							<v-icon>{{ isAddContentOrRecordIcon }}</v-icon>
						</v-btn>
						<div class="text">{{ isAddContentOrRecordText }}</div>
					</div>
				</template>
				<span>{{ isAddContentOrRecordTooltip }}</span>
			</v-tooltip>
		</div>
		<div
			v-if="conversationType === 'photos'"
			data-overflow-action="addPhotos"
			class="d-flex flex-column menu-action"
			:class="{ 'mobile-action-collapsed': isActionCollapsed('addPhotos') }"
			:aria-hidden="isActionCollapsed('addPhotos') ? 'true' : undefined">
			<v-tooltip z-index="2000" location="top" :open-on-hover="!DeviceUtility.isTouch" :open-on-click="false" :open-on-focus="false">
				<template #activator="{ props }">
					<div class="text-center d-flex flex-column justify-center align-center" v-bind="props">
						<v-btn v-test="'add-photos'" v-track="'add-photos-button'" icon elevation="0" @click="recordMore">
							<v-icon>icon-camera-stroke</v-icon>
						</v-btn>
						<div class="text">Take Photos</div>
					</div>
				</template>
				<span>Add new photos here</span>
			</v-tooltip>
		</div>
		<div
			v-if="entityType === 'conversation'"
			data-overflow-action="conversationShare"
			class="d-flex flex-column menu-action"
			:class="{ 'mobile-action-collapsed': isActionCollapsed('conversationShare') }"
			:aria-hidden="isActionCollapsed('conversationShare') ? 'true' : undefined">
			<v-tooltip
				z-index="2000"
				:disabled="DeviceUtility.isMobile"
				location="top"
				:open-on-hover="!DeviceUtility.isTouch"
				:open-on-click="false">
				<template #activator="{ props }">
					<div class="text-center d-flex flex-column justify-center align-center" v-bind="props">
						<ShareDialog :conversation-id="entity.id"></ShareDialog>
						<div class="text">Share</div>
					</div>
				</template>
				<span>Email this stuff to a user</span>
			</v-tooltip>
		</div>
		<div
			v-if="entityType === 'project'"
			data-overflow-action="addContent"
			class="d-flex flex-column menu-action"
			:class="{ 'mobile-action-collapsed': isActionCollapsed('addContent') }"
			:aria-hidden="isActionCollapsed('addContent') ? 'true' : undefined">
			<v-tooltip
				v-model="addContentTooltipOpen"
				location="top"
				:open-on-hover="!DeviceUtility.isTouch"
				:open-on-click="false"
				:close-on-content-click="true">
				<template #activator="{ props }">
					<div
						class="text-center d-flex flex-column justify-center align-center"
						:disabled="viewOnly"
						v-bind="props"
						@mouseleave="addContentTooltipOpen = false">
						<ProjectActionFAB
							v-if="entity.id"
							ref="collapsedAddContentFab"
							:project-id="entity.id"
							:project="entity"
							:disabled="isUploading"
							:should-show-project-action-f-a-b="showProjectFAB"
							@conversation-created="conversationCreated"
							@uploadCounts="(counts) => emit('uploadCounts', counts)"
							@fab-opened="handleAddContentFabOpened" />

						<div class="text custom-padding">Add Content</div>
					</div>
				</template>
				<span>Add new content here</span>
			</v-tooltip>
		</div>
		<!-- Project Link copy button -->
		<div
			v-if="entityType === 'project' && !isExternalEditor"
			data-overflow-action="projectLink"
			class="d-flex flex-column menu-action"
			:class="{ 'mobile-action-collapsed': isActionCollapsed('projectLink') }"
			:aria-hidden="isActionCollapsed('projectLink') ? 'true' : undefined">
			<v-tooltip location="top" :open-on-hover="!DeviceUtility.isTouch" :open-on-click="false">
				<template #activator="{ props }">
					<div class="text-center d-flex flex-column justify-center align-center">
						<v-btn
							v-bind="props"
							v-test="'copy-project-link'"
							v-track="'copy-project-link-button'"
							icon
							elevation="0"
							@click="copyProjectLink">
							<v-icon>icon-link-double</v-icon>
						</v-btn>
						<div class="text">Project Link</div>
					</div>
				</template>
				<span>Copy the project link to your clipboard</span>
			</v-tooltip>
		</div>
		<div
			v-if="!!entity && entityType === 'project'"
			data-overflow-action="projectShare"
			class="d-flex flex-column menu-action"
			:class="{ 'mobile-action-collapsed': isActionCollapsed('projectShare') }"
			:aria-hidden="isActionCollapsed('projectShare') ? 'true' : undefined">
			<v-tooltip
				v-model="shareTooltipOpen"
				location="top"
				:open-on-hover="!DeviceUtility.isTouch"
				:close-on-content-click="true"
				:open-on-click="false"
				:open-on-focus="!canShareProject">
				<template #activator="{ props }">
					<div v-bind="!canShareProject ? props : {}" :tabindex="canShareProject ? undefined : 0" class="text-center d-flex flex-column justify-center align-center">
						<ShareActionDialog
							v-if="canShareProject"
							ref="projectShareDialog"
							:btn-props="props"
							:project-name="entity.name"
							:project-id="entity.id"
							:staged-email="shareRequestEmail"
							:access="accessType"
							@shared="emit('refreshProject')"
							@access-changed="refreshProjectPermissions"
							@copy-link="copyLink"
							@dialog-opened="handleDialogOpened"
							@dialog-closed="handleShareDialogClosed($event, projectShareDialog)">
						</ShareActionDialog>
						<v-btn
							v-else
							v-test="'project-share'"
							icon="icon-share-contact-stroke"
							variant="plain"
							aria-label="Share"
							:aria-description="SHARE_PERMISSION_MESSAGE"
							disabled />
						<div class="text">Share</div>
					</div>
				</template>
				<span>{{ canShareProject ? "Share this project" : SHARE_PERMISSION_MESSAGE }}</span>
			</v-tooltip>
		</div>
		<div
			v-if="entityType === 'conversation'"
			data-overflow-action="upload"
			class="d-flex flex-column menu-action"
			:class="{ 'mobile-action-collapsed': isActionCollapsed('upload') }"
			:aria-hidden="isActionCollapsed('upload') ? 'true' : undefined">
			<v-tooltip z-index="2000" location="top" :open-on-hover="!DeviceUtility.isTouch" :open-on-click="false">
				<template #activator="{ props }">
					<div class="text-center d-flex flex-column justify-center align-center" v-bind="props">
						<v-btn
							v-test="'upload'"
							v-track="'upload-button'"
							aria-label="Upload"
							:disabled="isUploading"
							icon
							elevation="0"
							@click="openFilePicker">
							<v-icon>icon-cloud-stroke-upload</v-icon>
						</v-btn>
						<div class="text">Upload</div>
					</div>
				</template>
				<span>{{
					isUploading
						? " A file is still uploading. Please wait."
						: "Upload photos, documents, or videos from your device or desktop and store them here."
				}}</span>
			</v-tooltip>
		</div>
		<div data-overflow-action="more" class="d-flex flex-column menu-action">
			<v-menu
				v-model="moreOptionsOpened"
				:close-on-content-click="true"
				:close-on-click="true"
				:offset-y="true"
				:min-width="320"
				location="top"
				:z-index="2000"
				@input="handleMoreOptionsInput">
				<template #activator="{ props }">
					<v-btn
						v-bind="props"
						id="more-actions"
						v-test="'more-options'"
						v-track="'more-options-button'"
						icon="icon-dots-horizontal-more"
						elevation="0"
						aria-label="More">
					</v-btn>
					<div class="text">More</div>
				</template>

				<!-- To prevent list items from staying selected/active, do not use <v-list-item-group></v-list-item-group> -->

				<v-list rounded="lg" class="more-actions py-4">
					<v-list-item
						v-if="isActionCollapsed('upload')"
						v-test="'more-options-upload'"
						v-track="'more-options-upload-button'"
						class="mobile-collapsed-upload"
						:disabled="isUploading"
						@click="openFilePicker">
						<template v-slot:prepend>
							<v-icon>icon-cloud-stroke-upload</v-icon>
						</template>
						<v-list-item-title>Upload</v-list-item-title>
					</v-list-item>

					<v-tooltip v-if="isActionCollapsed('projectShare')" :disabled="canShareProject" location="top" :text="SHARE_PERMISSION_MESSAGE">
						<template #activator="{ props: tooltipProps }">
							<div v-bind="tooltipProps" :tabindex="canShareProject ? undefined : 0">
								<v-list-item
									v-test="'more-options-project-share'"
									v-track="'more-options-project-share-button'"
									class="mobile-collapsed-project-share"
									:disabled="!canShareProject"
									@click="openCollapsedProjectShare">
									<template v-slot:prepend>
										<v-icon>icon-share-contact-stroke</v-icon>
									</template>
									<v-list-item-title>Share</v-list-item-title>
								</v-list-item>
							</div>
						</template>
					</v-tooltip>

					<v-list-item
						v-if="isActionCollapsed('projectLink') && !isExternalEditor"
						v-test="'more-options-project-link'"
						v-track="'more-options-project-link-button'"
						class="mobile-collapsed-project-link"
						@click="copyProjectLink">
						<template v-slot:prepend>
							<v-icon>icon-link-double</v-icon>
						</template>
						<v-list-item-title>Copy Project Link</v-list-item-title>
					</v-list-item>

					<v-list-item
						v-if="isActionCollapsed('conversationShare')"
						v-test="'more-options-conversation-share'"
						v-track="'more-options-conversation-share-button'"
						class="mobile-collapsed-conversation-share"
						@click="openCollapsedConversationShare">
						<template v-slot:prepend>
							<v-icon>icon-share-contact-stroke</v-icon>
						</template>
						<v-list-item-title>Share</v-list-item-title>
					</v-list-item>

					<v-list-item
						v-if="isActionCollapsed('addContent')"
						v-test="'more-options-add-content'"
						v-track="'more-options-add-content-button'"
						class="mobile-collapsed-add-content"
						:disabled="isUploading"
						@click="openCollapsedAddContent">
						<template v-slot:prepend>
							<v-icon>icon-plus</v-icon>
						</template>
						<v-list-item-title>Add Content</v-list-item-title>
					</v-list-item>

					<v-list-item
						v-if="isActionCollapsed('addRecording')"
						v-test="'more-options-add-recording'"
						v-track="'more-options-add-recording-button'"
						class="mobile-collapsed-add-recording"
						@click="recordMore">
						<template v-slot:prepend>
							<v-icon>{{ isAddContentOrRecordIcon }}</v-icon>
						</template>
						<v-list-item-title>{{ isAddContentOrRecordText }}</v-list-item-title>
					</v-list-item>

					<v-list-item
						v-if="isActionCollapsed('addPhotos')"
						v-test="'more-options-add-photos'"
						v-track="'more-options-add-photos-button'"
						class="mobile-collapsed-add-photos"
						@click="recordMore">
						<template v-slot:prepend>
							<v-icon>icon-camera-stroke</v-icon>
						</template>
						<v-list-item-title>Take Photos</v-list-item-title>
					</v-list-item>

					<v-list-item
						v-if="isActionCollapsed('comments')"
						v-test="'more-options-comments'"
						v-track="'more-options-comments-button'"
						class="mobile-collapsed-comments"
						@click="handleCollapsedCommentsClick">
						<template v-slot:prepend>
							<v-icon>icon-chat-stroke</v-icon>
						</template>
						<v-list-item-title>Comments</v-list-item-title>
					</v-list-item>

					<v-list-item
						v-if="isActionCollapsed('runReport')"
						v-test="'more-options-run-report'"
						v-track="'more-options-run-report-button'"
						class="mobile-collapsed-run-report"
						@click="emit('runReport')">
						<template v-slot:prepend>
							<SeanIcon :height="24" :width="24" :is-running="scribeRunning" :enable-tooltip="false" />
						</template>
						<v-list-item-title>Run a Spark</v-list-item-title>
					</v-list-item>

					<v-list-item
						v-if="showCopySessionLink"
						id="copy-customer-link"
						v-test="'more-options-copy-invitation-link'"
						v-track="'more-options-copy-invitation-link'"
						class="copy-customer-link"
						@click="copyCallLink">
						<template v-slot:prepend>
							<v-icon>icon-link-double</v-icon>
						</template>
						<v-list-item-title>{{ copyInvitationLabel }}</v-list-item-title>
					</v-list-item>

					<v-list-item
						v-if="shouldShowResendInvitation"
						v-test="`more-options-resend-invitation-${entityType}`"
						v-track="`more-options-resend-invitation-${entityType}`"
						class="resend-invite"
						@click="sendInvitation">
						<template v-slot:prepend>
							<v-icon>icon-refresh-clockwise</v-icon>
						</template>
						<v-list-item-title>{{ resendInvitationLabel }}</v-list-item-title>
					</v-list-item>

					<v-list-item
						v-if="
							showCopyLink &&
							!(entityType === 'project' && isActionCollapsed('projectLink')) &&
							!(entityType === 'project' && isExternalEditor)
						"
						id="copy-session-link"
						v-test="`more-options-copy-${entityType}-link`"
						v-track="`more-options-copy-${entityType}-link`"
						class="copy-session-link"
						@click="() => copyLink()">
						<template v-slot:prepend>
							<v-icon>icon-link-double</v-icon>
						</template>
						<v-list-item-title v-if="entityType === 'project'"> Copy Project Link </v-list-item-title>
						<v-list-item-title v-else-if="isUserInitiatedRecording"> Copy Request Link </v-list-item-title>
						<v-list-item-title v-else-if="isUserInitiatedPhotos"> Copy Take Photos Link </v-list-item-title>
					</v-list-item>

					<v-list-item
						v-if="isSync"
						v-test="`more-options-join-${entityType}`"
						v-track="`more-options-join-${entityType}`"
						class="rejoin-conversation"
						@click="joinCall">
						<template v-slot:prepend>
							<v-icon>icon-rejoin-stroke</v-icon>
						</template>
						<v-list-item-title
							>{{ isScheduled ? "Join" : isNotStarted && !isSync ? "Join" : "Rejoin" }} Live Call</v-list-item-title
						>
					</v-list-item>

					<v-list-item
						v-test="`more-options-delete-${entityType}`"
						v-track="`more-options-delete-${entityType}`"
						class="delete-entity"
						:disabled="entityType === 'project' && !canDeleteProject"
						@click="deleteEntity">
						<template v-slot:prepend>
							<v-icon>icon-trash-full-stroke</v-icon>
						</template>
						<v-list-item-title>{{ deleteText }}</v-list-item-title>
					</v-list-item>
				</v-list>
			</v-menu>
		</div>
		<div
			v-if="display.mobile.value && (isActionCollapsed('conversationShare') || isActionCollapsed('projectShare'))"
			class="overflow-dialog-host">
			<ShareDialog
				v-if="isActionCollapsed('conversationShare')"
				ref="collapsedConversationShareDialog"
				:conversation-id="entity.id"
				:btn-props="{ style: 'display:none' }" />
			<ShareActionDialog
				v-if="isActionCollapsed('projectShare') && canShareProject"
				ref="collapsedProjectShareDialog"
				:project-name="entity.name"
				:project-id="entity.id"
				:staged-email="shareRequestEmail"
				:access="accessType"
				:btn-props="{ style: 'display:none' }"
				@shared="emit('refreshProject')"
				@access-changed="refreshProjectPermissions"
				@copy-link="copyLink"
				@dialog-opened="handleDialogOpened"
				@dialog-closed="handleShareDialogClosed($event, collapsedProjectShareDialog)" />
		</div>
	</div>
</template>

<script setup lang="ts">
	// Components
	import ProjectActionFAB from "@/components/ProjectActionFAB.vue";
	import ShareDialog from "@/components/ShareDialog.vue";
	import ShareActionDialog from "@/components/ShareActionDialog.vue";
	import SeanIcon from "@/components/icons/SeanIcon.vue";
	import { useScribeStore } from "@/stores/scribe";

	import { useFileUploadFlow } from "@/composables/useFileUploadFlow";
	import { EventBus } from "@/helpers/eventBus";
	import api from "@/api";
	import type { ConversationDelivery } from "@/api/index.d";
	import { AccessType } from "@/enums/AccessType";
	import DeviceUtility from "@/helpers/device";
	import { blurActiveElement, calculateTimeout, getMessageFromError } from "@/helpers/utilities";
	import { copyLinkToClipboard } from "@/helpers/clipboard";
	import { notifyInvitationDelivery } from "@/helpers/invitationSkipReasons";
	import { isEmailOnlyRepairState } from "@/helpers/smsDelivery";
	import { buildAcceptAttribute } from "@/helpers/fileUploads";
	import { logError, logInfo, logWarn } from "@/helpers/logError";
	import { couldNot, SHARE_PERMISSION_MESSAGE } from "@/helpers/userMessages";
	import uuid from "@/helpers/uuid";
	import { BaseEntityService } from "@/services/BaseEntityService";
	import { ConversationService } from "@/services/ConversationService";
	import { ProjectService } from "@/services/ProjectService";
	import { Conversation } from "@/types/Conversation";
	import type { EntityType } from "@/types/EntityType";
	import { Project } from "@/types/Project";
	import { computed, getCurrentInstance, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
	import { useResizeObserver } from "@vueuse/core";
	import { useDisplay } from "vuetify";
	import { useToast } from "vue-toastification";
	import { useRoute, useRouter } from "vue-router";

	import type { FileUploadCounts, UploadLifecycleEvent } from "@/types/FileUploadCounts";
	import { uploadBelongsToEntity } from "@/helpers/uploadEventScope";

	const instance = getCurrentInstance();
	const display = useDisplay();
	const toast = useToast();
	const router = useRouter();
	const route = useRoute();
	const service = ref<BaseEntityService<Project | Conversation> | null>(null);
	const isUploading = ref(false);
	const moreOptionsOpened = ref(false);
	const filePicker = ref<HTMLInputElement | null>(null);
	const actionMenu = ref<HTMLElement | null>(null);
	const shareTooltipOpen = ref(false);
	const addContentTooltipOpen = ref(false);
	const overflowMeasurementReady = ref(false);

	const COLLAPSIBLE_ACTION_IDS = [
		"upload",
		"projectShare",
		"conversationShare",
		"addContent",
		"addRecording",
		"addPhotos",
		"comments",
		"runReport",
		"projectLink"
	] as const;
	type CollapsibleActionId = (typeof COLLAPSIBLE_ACTION_IDS)[number];
	const collapsibleActionIdSet = new Set<string>(COLLAPSIBLE_ACTION_IDS);
	const collapsedActionIds = ref<CollapsibleActionId[]>([]);
	type ShareDialogInstance = { init: (event: { currentTarget: { blur: () => void } }) => void | boolean | Promise<void | boolean>; readonly loadGeneration?: number };
	type ProjectActionFabInstance = { open: () => void };
	const collapsedConversationShareDialog = ref<ShareDialogInstance | null>(null);
	const collapsedProjectShareDialog = ref<ShareDialogInstance | null>(null);
	const projectShareDialog = ref<ShareDialogInstance | null>(null);
	const shareRequestEmail = ref("");
	let activeShareRequest: { projectId: string; email: string } | null = null;
	let requestDialogOpen: { dialog: ShareDialogInstance; generation: number | undefined } | null = null;
	const collapsedAddContentFab = ref<ProjectActionFabInstance | null>(null);
	let overflowMeasurementFrame: number | null = null;
	let overflowMeasurementRun = 0;

	const scribeStore = useScribeStore();

	const scribeRunning = computed(() => scribeStore.anyAnalysisIsRunning);

	const { processFiles, prepareUploadStoragePersistence } = useFileUploadFlow();

	const props = withDefaults(
		defineProps<{
			me: any;
			entityType: EntityType;
			entity: Project | Conversation;
			freeTrialEnded: boolean;
			viewOnly: boolean;
			hasScribeFeature?: boolean;
			// A share-origin collaborator from a different org cannot copy the Project Link.
			isExternalEditor?: boolean;
			canShareProject?: boolean;
			canDeleteProject?: boolean;
			// LSP-20787 (E9): the session's SMS delivery state, so the generic resend can skip a blocked number.
			delivery?: ConversationDelivery | null;
		}>(),
		{
			isExternalEditor: false,
			canShareProject: false,
			canDeleteProject: false,
			delivery: null
		}
	);

	const emit = defineEmits<{
		(e: "toggleComments"): void;
		(e: "refreshProject"): void;
		(e: "refreshProjectPermissions", item: any, accessType: any): void;
		(e: "conversationCreated", conversationId: string): void;
		(e: "uploadCounts", counts: FileUploadCounts): void;
		(e: "runReport"): void;
		// LSP-20787: a resend went out, so the page refetches the delivery state once.
		// Bugbot: conversationId is the id the send was actually made for.
		(e: "invitationSent", conversationId: string): void;
	}>();

	watch(
		() => props.entity.id,
		() => {
			isUploading.value = false;
		}
	);

	const isCurrentUpload = (event?: UploadLifecycleEvent) =>
		event?.destinations.some((destination) => uploadBelongsToEntity(destination, props.entityType, props.entity.id));
	const uploadStartedHandler = (event?: UploadLifecycleEvent) => {
		if (isCurrentUpload(event)) isUploading.value = true;
	};
	const uploadFinishedHandler = (event?: UploadLifecycleEvent) => {
		if (isCurrentUpload(event)) isUploading.value = false;
	};

	useResizeObserver(actionMenu, () => {
		scheduleOverflowMeasurement();
	});

	onMounted(() => {
		if (props.entityType === "conversation") {
			service.value = new ConversationService();
		} else {
			service.value = new ProjectService();
		}
		EventBus.$on("UploadStarted", uploadStartedHandler);
		EventBus.$on("UploadFinished", uploadFinishedHandler);
		scheduleOverflowMeasurement({ hideUntilMeasured: display.mobile.value });
	});

	onUnmounted(() => {
		activeShareRequest = null;
		requestDialogOpen = null;
		EventBus.$off("UploadStarted", uploadStartedHandler);
		EventBus.$off("UploadFinished", uploadFinishedHandler);
		cancelScheduledOverflowMeasurement();
	});

	function isActionCollapsed(actionId: CollapsibleActionId) {
		return display.mobile.value && collapsedActionIds.value.includes(actionId);
	}

	function isCollapsibleActionId(actionId: string | undefined): actionId is CollapsibleActionId {
		return !!actionId && collapsibleActionIdSet.has(actionId);
	}

	function getActionWidth(actionElement: HTMLElement) {
		const actionRect = actionElement.getBoundingClientRect();
		const styles = window.getComputedStyle(actionElement);
		const marginLeft = Number.parseFloat(styles.marginLeft || "0") || 0;
		const marginRight = Number.parseFloat(styles.marginRight || "0") || 0;

		return Math.ceil((actionRect.width || actionElement.scrollWidth || actionElement.offsetWidth || 0) + marginLeft + marginRight);
	}

	function getMenuGapWidth(menuElement: HTMLElement) {
		const styles = window.getComputedStyle(menuElement);
		return Number.parseFloat(styles.columnGap || styles.gap || "0") || 0;
	}

	function cancelScheduledOverflowMeasurement() {
		overflowMeasurementRun++;

		if (overflowMeasurementFrame === null) return;

		window.cancelAnimationFrame(overflowMeasurementFrame);
		overflowMeasurementFrame = null;
	}

	function scheduleOverflowMeasurement(options: { hideUntilMeasured?: boolean } = {}) {
		if (!display.mobile.value) {
			collapsedActionIds.value = [];
			overflowMeasurementReady.value = true;
			cancelScheduledOverflowMeasurement();
			return;
		}

		if (options.hideUntilMeasured) {
			overflowMeasurementReady.value = false;
		}

		cancelScheduledOverflowMeasurement();
		const measurementRun = ++overflowMeasurementRun;
		overflowMeasurementFrame = window.requestAnimationFrame(() => {
			overflowMeasurementFrame = null;
			void nextTick(() => {
				if (measurementRun !== overflowMeasurementRun) return;

				overflowMeasurementFrame = window.requestAnimationFrame(() => {
					overflowMeasurementFrame = null;
					if (measurementRun !== overflowMeasurementRun) return;

					recalculateMobileOverflow();
				});
			});
		});
	}

	function recalculateMobileOverflow() {
		const menuElement = actionMenu.value;
		if (!menuElement || !display.mobile.value) {
			collapsedActionIds.value = [];
			overflowMeasurementReady.value = true;
			return;
		}

		const availableWidth = Math.floor(menuElement.getBoundingClientRect().width || menuElement.clientWidth || 0);
		if (availableWidth <= 0) {
			// No real width yet (pre-layout / hidden). Stay hidden and wait for the
			// ResizeObserver to re-fire with a real width, rather than revealing an
			// un-collapsed, overflowing row for a frame.
			return;
		}

		const actionElements = Array.from(menuElement.querySelectorAll<HTMLElement>("[data-overflow-action]"));
		const actionWidths = new Map<HTMLElement, number>();
		const perActionWidths: Record<string, number> = {};
		for (const actionElement of actionElements) {
			const actionWidth = getActionWidth(actionElement);
			actionWidths.set(actionElement, actionWidth);

			const actionId = actionElement.dataset.overflowAction;
			if (actionId) {
				perActionWidths[actionId] = actionWidth;
			}
		}

		const gapWidth = getMenuGapWidth(menuElement);
		let actionCount = actionElements.length;
		const totalWidthAllInline =
			Array.from(actionWidths.values()).reduce((sum, actionWidth) => sum + actionWidth, 0) + Math.max(actionCount - 1, 0) * gapWidth;
		let totalWidth = totalWidthAllInline;
		const nextCollapsedActionIds: CollapsibleActionId[] = [];

		for (const actionElement of [...actionElements].reverse()) {
			if (totalWidth <= availableWidth) break;

			const actionId = actionElement.dataset.overflowAction;
			if (!isCollapsibleActionId(actionId)) continue;

			nextCollapsedActionIds.push(actionId);
			totalWidth -= (actionWidths.get(actionElement) ?? 0) + (actionCount > 1 ? gapWidth : 0);
			actionCount--;
		}

		if (!areActionIdsEqual(collapsedActionIds.value, nextCollapsedActionIds)) {
			logInfo("EntityActions mobile overflow collapsed actions changed", {
				availableWidth,
				totalWidthAllInline,
				perActionWidths,
				collapsedActionIds: nextCollapsedActionIds
			});
		}

		collapsedActionIds.value = nextCollapsedActionIds;
		overflowMeasurementReady.value = true;
	}

	function areActionIdsEqual(currentActionIds: readonly CollapsibleActionId[], nextActionIds: readonly CollapsibleActionId[]) {
		return (
			currentActionIds.length === nextActionIds.length &&
			currentActionIds.every((actionId, index) => actionId === nextActionIds[index])
		);
	}

	function getCollapsedActivatorEvent(event?: MouseEvent | KeyboardEvent) {
		const currentTarget = event?.currentTarget;
		if (currentTarget instanceof HTMLElement) {
			return { currentTarget };
		}

		return { currentTarget: { blur: blurActiveElement } };
	}

	function openCollapsedConversationShare(event?: MouseEvent | KeyboardEvent) {
		moreOptionsOpened.value = false;
		void collapsedConversationShareDialog.value?.init(getCollapsedActivatorEvent(event));
	}

	function openCollapsedProjectShare(event?: MouseEvent | KeyboardEvent) {
		if (!props.canShareProject) return;
		moreOptionsOpened.value = false;
		void collapsedProjectShareDialog.value?.init(getCollapsedActivatorEvent(event));
	}

	const sharingRequest = computed<{ projectId: string; email: string } | null>((previousRequest) => {
		if (route.name !== "project" || props.entityType !== "project" || !props.canShareProject || props.viewOnly || props.freeTrialEnded ||
			route.params.id !== props.entity.id || route.query.share !== props.entity.id) return null;
		const email = typeof route.query.requestAccessEmail === "string" ? route.query.requestAccessEmail : "";
		if (previousRequest?.projectId === props.entity.id && previousRequest.email === email) return previousRequest;
		return { projectId: props.entity.id, email };
	});
	function reportSharingRequestFailure(error: unknown, projectId: string) {
		logError("EntityActions: sharing request could not be opened", error, { projectId });
		const message = couldNot("open this sharing request");
		toast.error(message, { timeout: calculateTimeout(message) });
	}
	async function consumeSharingRequest(request: { projectId: string; email: string } | null) {
		if (!request || request !== activeShareRequest || sharingRequest.value !== request) return;
		try {
			const query = { ...route.query };
			delete query.share;
			delete query.requestAccessEmail;
			await router.replace({ query });
		} catch (error) {
			if (request === activeShareRequest && sharingRequest.value === request) reportSharingRequestFailure(error, request.projectId);
		}
	}
	function handleShareDialogClosed(closed: { requestUnavailable: boolean; loadGeneration: number }, dialog: ShareDialogInstance | null) {
		const opening = requestDialogOpen;
		if (activeShareRequest && (!opening || opening.dialog !== dialog || opening.generation !== closed.loadGeneration)) return;
		shareRequestEmail.value = "";
		if (!closed.requestUnavailable) void consumeSharingRequest(sharingRequest.value);
	}
	watch([sharingRequest, overflowMeasurementReady, projectShareDialog, collapsedProjectShareDialog], async ([request]) => {
		if (!request) { activeShareRequest = null; requestDialogOpen = null; return; }
		const dialog = isActionCollapsed("projectShare") ? collapsedProjectShareDialog.value : projectShareDialog.value;
		if (!overflowMeasurementReady.value || !dialog || activeShareRequest === request) return;
		activeShareRequest = request;
		requestDialogOpen = null;
		const isCurrent = () => activeShareRequest === request && sharingRequest.value === request;
		shareRequestEmail.value = request.email;
		await nextTick();
		if (!isCurrent()) return;
		try {
			const pendingOpen = dialog.init(getCollapsedActivatorEvent());
			requestDialogOpen = { dialog, generation: dialog.loadGeneration };
			const requestReady = await pendingOpen;
			if (requestReady !== true || !isCurrent()) return;
			await consumeSharingRequest(request);
		} catch (error) {
			if (!isCurrent()) return;
			reportSharingRequestFailure(error, request.projectId);
		}
	}, { flush: "post", immediate: true });

	// Common computed properties

	const deleteText = computed(() => {
		if (props.entityType === "conversation") {
			return "Delete My Stuff";
		}
		return "Delete Project";
	});

	// Conversation computed properties

	const accept = computed(() => {
		return buildAcceptAttribute({ allowDocs: true });
	});

	const conversationType = computed(() => {
		if (props.entityType === "conversation") {
			return (props.entity as Conversation).type;
		}

		return null;
	});

	watch(
		[() => props.entityType, conversationType, () => props.viewOnly, () => props.hasScribeFeature],
		() => {
			scheduleOverflowMeasurement({ hideUntilMeasured: display.mobile.value });
		},
		{ flush: "post" }
	);

	watch(
		() => display.mobile.value,
		() => {
			scheduleOverflowMeasurement({ hideUntilMeasured: display.mobile.value });
		},
		{ flush: "post" }
	);

	const isCustomerAsyncRequest = computed(() => {
		if (props.entityType === "conversation") {
			return !!(props.entity as Conversation).customerUrl && (props.entity as Conversation).sourceType !== "FORM";
		}

		return false;
	});

	const isAddContentOrRecordIcon = computed(() => {
		return isCustomerAsyncRequest.value ? "icon-plus" : "icon-add-recording-stroke";
	});

	const isAddContentOrRecordText = computed(() => {
		return isCustomerAsyncRequest.value ? "Add Content" : "Record";
	});

	const isAddContentOrRecordTooltip = computed(() => {
		return isCustomerAsyncRequest.value ? "Add new content here" : "Add a new recording here";
	});

	const isSync = computed(() => {
		return (props.entity as Conversation).type?.toLowerCase() === "sync";
	});

	const isAsync = computed(() => {
		return (props.entity as Conversation).type?.toLowerCase() === "async";
	});

	const isScheduled = computed(() => {
		return (props.entity as Conversation).status?.toLowerCase() === "scheduled";
	});

	const isNotStarted = computed(() => {
		return (props.entity as Conversation).status?.toLowerCase() === "notstarted";
	});

	const showCopySessionLink = computed(() => {
		// Admin-initiated recordings render their own "Copy Request Link" item below
		// (driven by showCopyLink) — suppress this one to avoid two identically labeled
		// items that perform different actions.
		return isSync.value || (isAsync.value && !isSelfRecording.value && !isUserInitiatedRecording.value);
	});

	const isWebformConversation = computed(() => {
		if (props.entityType !== "conversation") {
			return false;
		}

		return (props.entity as Conversation).sourceType === "FORM";
	});

	const copyInvitationLabel = computed(() => {
		if (isAsync.value && isWebformConversation.value) {
			return "Copy Recording Link";
		}

		if (isAsync.value) {
			return "Copy Request Link";
		}

		return "Copy Invitation Link";
	});

	const resendInvitationLabel = computed(() => {
		if (isAsync.value && !isWebformConversation.value) {
			return "Resend Request";
		}

		return "Resend Invitation";
	});

	const isUserInitiatedRecording = computed(() => {
		return (
			isAsync.value &&
			(props.entity as Conversation).initiationType?.toLowerCase() === "admin" &&
			(props.entity as Conversation).customerUrl === ""
		);
	});

	const isUserInitiatedPhotos = computed(() => {
		return (props.entity as Conversation).type === "photos";
	});

	const hasResendableParticipant = computed(() => {
		if (props.entityType !== "conversation") {
			return false;
		}

		const conversation = props.entity as Conversation;

		return (conversation.Participants ?? []).some((participant) => {
			const hasCustomerId =
				typeof participant.customerId === "string" ? participant.customerId.trim().length > 0 : !!participant.customerId;
			const hasDeliverableChannel =
				(participant.notificationSMS && !!participant.phone) || (participant.notificationEmail && !!participant.email);

			return hasCustomerId && hasDeliverableChannel;
		});
	});

	const shouldShowResendInvitation = computed(() => {
		return (isSync.value || (isAsync.value && !isUserInitiatedRecording.value)) && hasResendableParticipant.value;
	});

	const showCopyLink = computed(() => {
		return props.entityType === "project" || isUserInitiatedRecording.value || isUserInitiatedPhotos.value;
	});

	const isSelfRecording = computed(() => {
		return (props.entity as Conversation).initiator?.toLowerCase() === "recording";
	});

	// Project computed properties

	const showProjectFAB = computed(() => {
		return props.entityType === "project" && !props.freeTrialEnded;
	});

	const accessType = computed(() => {
		if (!props.viewOnly) {
			return AccessType.Editor;
		}

		return AccessType.Viewer;
	});

	// Conversation functions

	function joinCall() {
		if (props.freeTrialEnded) {
			(instance?.proxy?.$root as any).$freeTrialEndedDialog.open(props.me.organization);
		} else {
			const win = window.open((props.entity as Conversation).creatorUrl as string, "_blank");
			if (!win) {
				const message = "Unable to open a new window. Please check your popup blocker settings or close some tabs and try again.";
				toast.error(message, { timeout: calculateTimeout(message) });
			}
		}
	}

	async function copyCallLink() {
		// Keep the toast noun in sync with the menu label so Webform sessions
		// (labelled "Copy Recording Link") don't render a "recording request" toast.
		const sessionTypeStr = isSync.value ? "live call" : isWebformConversation.value ? "recording" : "recording request";
		const conversationId = (props.entity as Conversation).id;

		// Start the refresh WITHOUT awaiting it. The refetch is required because the backend
		// regenerates expired invitation URLs on fetch (LSP-13615), but awaiting it here would
		// spend Safari's transient user activation and make the clipboard write fail on iOS
		// every time (LSP-18938). copyLinkToClipboard hands this promise to the clipboard
		// instead, which keeps the activation alive while it resolves.
		try {
			// Inside the try so a synchronous throw is reported too. api.getConversation is an async
			// function today and so can only reject, but an unreported throw here would surface as a
			// rejected Vue event-handler promise with no toast at all — the exact silent failure this
			// ticket is about. Constructing the promise here still leaves no await before the copy.
			const urlPromise = api.getConversation(conversationId).then((refreshedConversation: Conversation) => {
				const customerUrl = refreshedConversation?.customerUrl;
				if (!customerUrl) {
					throw new Error(`Conversation ${conversationId} returned no customerUrl.`);
				}

				return customerUrl;
			});

			await copyLinkToClipboard({
				urlPromise,
				noun: sessionTypeStr,
				toast,
				successMessage: `The contact's link to this ${sessionTypeStr} has been copied to the clipboard.`
			});
		} catch (err) {
			// Reached when no usable link could be produced — the refresh failed, or it returned
			// data without a usable customerUrl (either our own check above or the helper's
			// blank-URL guard). Never for a clipboard failure: copyLinkToClipboard handles that
			// itself with the manual-copy fallback. Either way there is no link to hand back.
			logError(`[CopyLink] Could not retrieve the contact's ${sessionTypeStr} link.`, err, {
				handler: "copyCallLink",
				conversationId,
				sessionTypeStr
			});
			const message = `Something went wrong getting the contact's link to this ${sessionTypeStr}. Please try again.`;
			toast.error(message, { timeout: calculateTimeout(message) });
		}
	}

	async function copyLink() {
		if (props.entityType === "project") {
			await copyProjectLink();
			return;
		}

		// Keep toast nouns aligned with the menu labels ("Copy Request Link",
		// "Copy Take Photos Link") so users see consistent wording end-to-end.
		const noun = isUserInitiatedRecording.value ? "request" : "Take Photos";
		const creatorUrl = (props.entity as Conversation).creatorUrl;
		const unavailableMessage = `Something went wrong getting the ${noun} link. Please try again.`;

		if (!creatorUrl) {
			// Diagnostic for a previously silent dead end: without this the menu tap looked
			// identical to a clipboard permission failure in logs.
			logWarn(`[CopyLink] Cannot copy the ${noun} link: the conversation has no creatorUrl.`, {
				handler: "copyLink",
				conversationId: props.entity?.id,
				noun
			});
			toast.error(unavailableMessage, { timeout: calculateTimeout(unavailableMessage) });
			return;
		}

		try {
			await copyLinkToClipboard({ urlPromise: Promise.resolve(creatorUrl), noun, toast });
		} catch (err) {
			logError(`[CopyLink] Could not copy the ${noun} link.`, err, {
				handler: "copyLink",
				conversationId: props.entity?.id,
				noun
			});
			toast.error(unavailableMessage, { timeout: calculateTimeout(unavailableMessage) });
		}
	}

	async function recordMore() {
		if (props.freeTrialEnded) {
			(instance?.proxy?.$root as any).$freeTrialEndedDialog.open(props.me.organization);
		} else {
			const win = window.open("", "_blank");
			if (win) {
				win.location = (props.entity as Conversation).creatorUrl as string;
			} else {
				const message = "Unable to open a new window. Please check your popup blocker settings or close some tabs and try again.";
				toast.error(message, { timeout: calculateTimeout(message) });
			}
		}

		blurActiveElement();
	}

	async function sendInvitation() {
		if (props.freeTrialEnded) {
			(instance?.proxy?.$root as any).$freeTrialEndedDialog.open(props.me.organization);
		} else {
			// Toast noun follows the menu label: "Resend Request" for async
			// non-Webform sessions, "Resend Invitation" for everything else.
			const noun = isAsync.value && !isWebformConversation.value ? "request" : "invitation";
			// LSP-20787 (E9): while the header shows a blocked number, "Resend Invitation" goes by email only —
			// the same request-scoped channel the repair box uses. Every other state is today's resend. Bugbot:
			// gate on the org's email-invitations setting too, the same source EntityHeader/EntityDetailPage
			// read — the API refuses channel "email" (400) when it's off, so that org falls back to the normal
			// no-channel resend instead (D22 already skips re-texting a blocked, unchanged number there).
			const emailInvitationsAllowed = props.me?.organization?.allowEmailInvitations !== false;
			const emailOnly = isEmailOnlyRepairState(props.delivery?.state) && emailInvitationsAllowed;
			const conversationId = (props.entity as Conversation).id;
			try {
				// One argument when there is no channel: an existing spec pins toHaveBeenCalledWith("0").
				const response = emailOnly ? await api.sendInvitation(conversationId, "email") : await api.sendInvitation(conversationId);
				// LSP-20787: the page refetches the delivery state once — also after a partial send, because the
				// channel that was not skipped did go out. Bugbot: carry conversationId so a send that finishes
				// after an in-place session switch cannot refresh the session the page now shows instead.
				emit("invitationSent", conversationId);
				// LSP-19707: warn for EVERY channel the API reports as skipped, SMS and email alike. This
				// used to check one SMS reason (`participantSmsNotificationOff`); every other reason — and
				// every suppressed email, since `emailReason` was never read — fell through to the green
				// success toast and told the agent a customer had been contacted when they had not.
				// Structure/wording per the ticket author (comment 151656): a heading plus one bullet per
				// blocked channel, and a partial send says what did go out instead of reading as failure.
				const message = emailOnly ? `The ${noun} has been sent by email.` : `The ${noun} has been re-sent to the contact.`;
				notifyInvitationDelivery(toast, response.invitations, message, emailOnly ? "email" : undefined);
			} catch (err) {
				const fallback = `Failed to re-send ${noun} to the contact.`;
				// M1: the email-only resend's refusals ("Email invitations are turned off…", "The contact has no email address…")
				// are the rep's next step, so they reach the toast as the repair panel's do; today's resend keeps its fixed line.
				const message = emailOnly ? getMessageFromError(err, fallback) : fallback;
				toast.error(message, { timeout: calculateTimeout(message) });
			}
		}
	}

	function anonymousParticipants() {
		//We need to disable for form filled async conversations and Start a recording async conversations
		if ((props.entity as Conversation).sourceType === "FORM") {
			return [];
		}
		return (props.entity as Conversation).Participants?.filter((x) => !x.userId);
	}

	function anonymousParticipantsWithContactInfo() {
		return anonymousParticipants()?.filter((x) => (x.email && x.notificationEmail) || (x.phone && x.notificationSMS)) ?? [];
	}

	function anonymousParticipantsText() {
		return anonymousParticipantsWithContactInfo()?.length
			? anonymousParticipants()
					?.map((x) => x.firstname || x.phone)
					.join(",")
			: "- Inbound -";
	}

	async function openFilePicker() {
		if (props.freeTrialEnded) {
			(instance?.proxy?.$root as any).$freeTrialEndedDialog.open(props.me.organization);
		} else {
			prepareUploadStoragePersistence(props.entity.id);
			filePicker.value?.click();
		}
		blurActiveElement();
	}

	function handleFileChange(event: Event) {
		const target = event.target as HTMLInputElement;
		if (target.files?.length) {
			filesChanged(Array.from(target.files));
		}
	}

	async function filesChanged(files: File[]) {
		const uploadEntity = { ...props.entity };
		const uploadEntityType = props.entityType;
		const destination = {
			conversationId: uploadEntity.id,
			projectId: uploadEntityType === "project" ? uploadEntity.id : (uploadEntity as Conversation).projectId
		};
		// Callback to emit counts after each file upload
		const onFileUploaded = (fileType: "image" | "document" | "video") => {
			const counts = {
				...destination,
				images: fileType === "image" ? 1 : 0,
				documents: fileType === "document" ? 1 : 0,
				videos: fileType === "video" ? 1 : 0
			};
			// The reused parent applies destination ownership, including a session's owning project.
			emit("uploadCounts", counts);
		};

		if (files.length === 0) {
			return;
		}

		isUploading.value = true;
		try {
			await processFiles(files, uploadEntity, { onFileUploaded });
		} catch (error) {
			console.error(error, {
				extra: {
					file: "EntityActions.vue",
					method: "filesChanged"
				},
				tags: {
					[`${props.entityType}Id`]: props.entity.id
				}
			});
		} finally {
			if (props.entity.id === uploadEntity.id && props.entityType === uploadEntityType) isUploading.value = false;
			if (filePicker.value) {
				filePicker.value.value = "";
			}
		}
	}

	// Project functions

	async function copyProjectLink() {
		// Started without an intervening await so the clipboard write still runs inside
		// Safari's user activation — see copyCallLink and LSP-18938.
		try {
			// Inside the try for the same reason as copyCallLink; still no await before the copy.
			const urlPromise = getShareUrlOfProject().then((url) => {
				if (!url) {
					throw new Error(`Project ${props.entity?.id} produced no share URL.`);
				}

				return url;
			});

			await copyLinkToClipboard({ urlPromise, noun: "project", toast });
		} catch (err) {
			logError("[CopyLink] Could not build the project share link.", err, {
				handler: "copyProjectLink",
				projectId: props.entity?.id
			});
			const message = "Something went wrong getting the project link. Please try again.";
			toast.error(message, { timeout: calculateTimeout(message) });
		}

		blurActiveElement();
	}

	async function getShareUrlOfProject() {
		let project: Project = props.entity as Project;

		if (!project.shareId) {
			// create a uuid for sharing
			const shareId = uuid.uuidv4();

			project = await api.updateProject(props.entity.id, {
				shareId,
				genericAccessType: AccessType.Viewer
			});

			// TODO: Emit to parent to refresh the project
		}

		return project?.shareUrl;
	}

	function refreshProjectPermissions(item: any, accessType: any) {
		emit("refreshProjectPermissions", item, accessType);
	}

	function conversationCreated(conversationId: string) {
		console.log("EntityActions conversationCreated " + conversationId);
		emit("conversationCreated", conversationId);
	}

	// Common functions

	async function deleteEntity() {
		if (props.entityType === "project" && !props.canDeleteProject) return;
		if (props.freeTrialEnded) {
			(instance?.proxy?.$root as any).$freeTrialEndedDialog.open(props.me.organization);
		} else {
			if (
				await (instance?.proxy?.$root as any).$confirm.deleteOpen(
					props.entityType === "conversation" ? "You're about to delete your stuff" : "You're about to delete a project",
					props.entityType === "conversation"
						? "Are you sure? This media may be attached to a checklist. The media will be removed from the project and associated checklists."
						: "Are you sure? This project will be removed."
				)
			) {
				try {
					await service.value?.deleteEntity((props.entity as Conversation).id);
					done();
				} catch (err) {
					const message =
						getMessageFromError(err) ||
						`We were unable to delete your ${props.entityType === "conversation" ? "stuff" : "project"}. Please try again.`;
					toast.error(message, { timeout: calculateTimeout(message) });
				}
			}
		}
	}

	function done() {
		// Deleted detail routes point at an entity that no longer exists.
		// For list/direct deletes, push explicitly because iOS Safari can leave users on a dead page after router.go(-1).
		const listSourceByEntityType: Partial<Record<EntityType, string>> = {
			conversation: "conversations",
			project: "projects"
		};
		const listSource = listSourceByEntityType[props.entityType];

		if (route.query.from && route.query.from !== listSource) {
			router.go(-1);
			return;
		}

		if (props.entityType === "conversation") {
			router.push("/conversations");
			return;
		}

		if (props.entityType === "project") {
			router.push("/projects");
			return;
		}

		router.push("/");
	}

	function handleMoreOptionsInput(value: boolean) {
		moreOptionsOpened.value = value;
	}

	function handleCollapsedCommentsClick() {
		emit("toggleComments");
		blurActiveElement();
	}

	function openCollapsedAddContent(event?: MouseEvent | KeyboardEvent) {
		event?.stopPropagation();
		handleAddContentFabOpened();

		if (isUploading.value) {
			return;
		}

		if (!showProjectFAB.value) {
			(instance?.proxy?.$root as any).$freeTrialEndedDialog.open(props.me.organization);
			blurActiveElement();
			return;
		}

		// Close the More menu first so its overlay is gone, then (next tick) open
		// the INLINE toolbar FAB. When Add Content is collapsed that inline item is
		// position:absolute; visibility:hidden at the toolbar's top-right (on-screen,
		// next to More) and stays mounted in the toolbar, so it survives the menu
		// closing and its own activator slot anchors the menu correctly.
		moreOptionsOpened.value = false;
		void nextTick(() => {
			collapsedAddContentFab.value?.open();
			blurActiveElement();
		});
	}

	function handleDialogOpened() {
		shareTooltipOpen.value = false;
	}

	function handleAddContentFabOpened() {
		addContentTooltipOpen.value = false;
	}
</script>

<style lang="scss">
	.menu {
		flex: 1;
		color: #000;
		gap: 8px;

		&.mobile-overflow-toolbar {
			flex: 1 1 auto;
			min-width: 0;
			max-width: 100%;
			overflow: hidden;
			position: relative;
			justify-content: flex-end;

			&:not(.mobile-overflow-toolbar--ready) {
				visibility: hidden;
			}

			// Uniform width so the icons are evenly spaced regardless of label
			// length ("Run a Spark" vs "Share"). ~average of the natural widths, so
			// the total (and thus the collapse count) is unchanged; long labels
			// center + wrap to two lines under the icon.
			.menu-action {
				flex: 0 0 auto;
				width: 2.5rem;
				min-width: 2.5rem;
			}

			.mobile-action-collapsed {
				position: absolute;
				top: 0;
				inset-inline-end: 0;
				visibility: hidden;
				pointer-events: none;
			}

			.overflow-dialog-host {
				position: absolute;
				width: 0;
				height: 0;
				overflow: hidden;
			}
		}

		.menu-action {
			flex: 1;
			min-width: 0;
			align-items: center;
			font-size: 0.625rem;
			font-weight: 600;

			.text {
				margin-top: 4px;
				font-weight: 700;
				color: rgba(var(--v-theme-ink), 0.6);
				text-align: center;
				max-width: 100%;
				overflow-wrap: break-word;

				@media #{map.get($display-breakpoints, 'sm-and-down')} {
					font-size: 0.5rem;
				}
			}

			.v-btn {
				background-color: transparent;
				height: 36px;
				width: 36px;
				border: 2px solid rgb(var(--v-theme-brand));

				@media #{map.get($display-breakpoints, 'sm-and-down')} {
					height: 32px;
					width: 32px;
				}

				.v-icon {
					font-size: 1.5rem;
					color: rgb(var(--v-theme-brand));

					@media #{map.get($display-breakpoints, 'sm-and-down')} {
						font-size: 1.25rem;
					}
				}
			}
		}
	}

	.more-actions .delete-entity .v-icon {
		color: #f94366;
	}

	.more-actions {
		.resend-invite .v-icon {
			color: rgb(var(--v-theme-brand));
		}
		.rejoin-conversation .v-icon {
			color: rgb(var(--v-theme-brand));
		}
	}
</style>
